<template>
  <div class="col-span-12">
    <div :class="[
      'rounded-xl p-6 h-full flex flex-col',
      darkMode ? 'bg-[#111722]' : 'bg-white'
    ]">
      <h2 :class="[
        'text-2xl font-bold mb-6',
        darkMode ? 'text-white' : 'text-gray-900'
      ]">{{ $t('history.streamHistory') }}</h2>
      
      <!-- 加载状态 -->
      <div v-if="loading" class="text-center py-8 flex-1 flex flex-col items-center justify-center">
        <div class="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mb-3"></div>
        <p :class="['text-sm', darkMode ? 'text-slate-400' : 'text-gray-600']">{{ $t('history.loadingStreamHistory') }}</p>
      </div>
      
      <!-- 错误状态 -->
      <div v-else-if="error" class="text-center py-8 flex-1 flex flex-col items-center justify-center">
        <span class="material-symbols-outlined text-red-500 text-4xl mb-3">error</span>
        <p :class="['text-sm mb-2', darkMode ? 'text-red-400' : 'text-red-600']">{{ $t('history.loadFailed') }}: {{ error }}</p>
        <button @click="fetchSessionStats" class="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 transition-colors">
          {{ $t('common.retry') }}
        </button>
      </div>
      
      <!-- 无数据状态 -->
      <div v-else-if="!loading && sessionStats.length === 0" class="text-center py-8 flex-1 flex flex-col items-center justify-center">
        <span :class="['material-symbols-outlined text-4xl mb-3', darkMode ? 'text-slate-600' : 'text-gray-400']">history</span>
        <p :class="['text-sm', darkMode ? 'text-slate-400' : 'text-gray-600']">{{ $t('history.noStreamHistory') }}</p>
      </div>
      
      <!-- 历史记录列表 -->
      <div v-else class="flex-1 overflow-y-auto custom-scrollbar">
        <div class="space-y-4 pr-2">
          <div v-for="session in sessionStats" :key="session.sessionId"
               :class="[
                 'rounded-lg p-4 transition-colors cursor-pointer',
                 darkMode 
                   ? 'bg-[#232f48] hover:bg-[#2a3752]' 
                   : 'bg-gray-100 hover:bg-gray-200'
               ]">
            <!-- 移动端布局 -->
            <div class="block md:flex md:items-center md:justify-between">
              <div class="flex items-center gap-4 mb-4 md:mb-0">
                <!-- 直播缩略图 -->
                <div :class="[
                  'rounded w-16 h-12 md:w-20 md:h-12 flex items-center justify-center overflow-hidden flex-shrink-0',
                  darkMode ? 'bg-slate-700' : 'bg-gray-300'
                ]">
                  <img v-if="session.coverUrl" :src="session.coverUrl" :alt="session.title || $t('history.liveCover')" class="w-full h-full object-cover">
                  <span :class="[
                    'material-symbols-outlined text-lg',
                    darkMode ? 'text-slate-400' : 'text-gray-500'
                  ]">play_circle</span>
                </div>

                <!-- 直播信息 -->
                <div class="min-w-0 flex-1">
                  <h3 :class="[
                    'font-semibold text-sm md:text-base truncate',
                    darkMode ? 'text-white' : 'text-gray-900'
                  ]">{{ session.title || $t('history.liveSession', { sessionId: session.sessionId }) }}</h3>
                  <div class="flex flex-wrap items-center gap-2 md:gap-4 mt-1">
                    <span :class="[
                      'text-xs md:text-sm',
                      darkMode ? 'text-slate-400' : 'text-gray-600'
                    ]">{{ formatDate(session.startTime) }}</span>
                    <span :class="[
                      'text-xs md:text-sm',
                      darkMode ? 'text-slate-400' : 'text-gray-600'
                    ]">{{ formatDuration(session.duration) }}</span>
                    <span :class="['px-2 py-0.5 rounded-full text-xs font-medium', getStatusClass(session.status)]">
                      {{ getStatusText(session.status) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- 统计数据 -->
              <div class="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                <!-- 统计数据网格 -->
                <div class="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 flex-1">
                  <div class="text-center">
                    <p :class="['text-xs', darkMode ? 'text-slate-400' : 'text-gray-600']">{{ $t('history.totalMessages') }}</p>
                    <p :class="['font-semibold text-sm', darkMode ? 'text-white' : 'text-gray-900']">{{ session.statsSummary ? session.statsSummary.totalMessages : 0 }}</p>
                  </div>
                  <div class="text-center">
                    <p :class="['text-xs', darkMode ? 'text-slate-400' : 'text-gray-600']">{{ $t('history.maxOnline') }}</p>
                    <p :class="['font-semibold text-sm', darkMode ? 'text-white' : 'text-gray-900']">{{ session.statsSummary ? session.statsSummary.maxOnlineUsers : 0 }}</p>
                  </div>
                  <div class="text-center">
                    <p :class="['text-xs', darkMode ? 'text-slate-400' : 'text-gray-600']">{{ $t('history.giftValue') }}</p>
                    <p :class="['font-semibold text-sm flex items-center justify-center', darkMode ? 'text-white' : 'text-gray-900']">
                      <i class="fas fa-coins text-yellow-400 mr-1 text-xs"></i>
                      {{ formatNumber(session.statsSummary ? session.statsSummary.totalGiftValue : 0) }}
                    </p>
                  </div>
                  <div class="text-center">
                    <p :class="['text-xs', darkMode ? 'text-slate-400' : 'text-gray-600']">{{ $t('history.giftCount') }}</p>
                    <p :class="['font-semibold text-sm', darkMode ? 'text-white' : 'text-gray-900']">{{ formatNumber(session.statsSummary ? session.statsSummary.totalGiftNum : 0) }}</p>
                  </div>
                </div>

                <!-- 操作按钮 -->
                <button @click.stop="viewSessionDetails(session)"
                        class="px-3 py-2 md:px-4 md:py-2 bg-primary text-white text-xs md:text-sm rounded-lg hover:bg-primary/90 transition-colors whitespace-nowrap">
                  {{ $t('history.viewDetails') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 分页 -->
      <div v-if="!loading && !error && sessionStats.length > 0" class="mt-6">
        <!-- 移动端分页 -->
        <div class="block md:hidden">
          <div class="flex justify-center items-center gap-2 mb-3">
            <button @click="prevPage" :disabled="pageIndex <= 1"
                    :class="[
                      'px-3 py-2 rounded-lg transition-colors text-sm',
                      pageIndex <= 1 
                        ? (darkMode ? 'bg-[#232f48] text-slate-400 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed')
                        : (darkMode ? 'bg-[#232f48] text-slate-400 hover:bg-primary hover:text-white' : 'bg-gray-200 text-gray-600 hover:bg-primary hover:text-white')
                    ]">
              <span class="material-symbols-outlined text-sm">chevron_left</span>
            </button>

            <span :class="['text-sm', darkMode ? 'text-slate-400' : 'text-gray-600']">
              {{ pageIndex }} / {{ totalPages }}
            </span>

            <button @click="nextPage" :disabled="pageIndex >= totalPages"
                    :class="[
                      'px-3 py-2 rounded-lg transition-colors text-sm',
                      pageIndex >= totalPages
                        ? (darkMode ? 'bg-[#232f48] text-slate-400 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed')
                        : (darkMode ? 'bg-[#232f48] text-slate-400 hover:bg-primary hover:text-white' : 'bg-gray-200 text-gray-600 hover:bg-primary hover:text-white')
                    ]">
              <span class="material-symbols-outlined text-sm">chevron_right</span>
            </button>
          </div>

          <div class="flex justify-center items-center gap-2">
            <span :class="['text-xs', darkMode ? 'text-slate-400' : 'text-gray-600']">{{ $t('common.perPage') }}:</span>
            <select v-model="pageSize" @change="handlePageSizeChange"
                    :class="[
                      'border text-xs rounded-md px-2 py-1 focus:ring-primary focus:border-primary',
                      darkMode ? 'bg-[#232f48] border-slate-700 text-white' : 'bg-gray-100 border-gray-300 text-gray-900'
                    ]">
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>

        <!-- 桌面端分页 -->
        <div class="hidden md:flex justify-center items-center gap-2">
          <button @click="prevPage" :disabled="pageIndex <= 1"
                  :class="[
                    'px-3 py-1 rounded transition-colors',
                    pageIndex <= 1
                      ? (darkMode ? 'bg-[#232f48] text-slate-400 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed')
                      : (darkMode ? 'bg-[#232f48] text-slate-400 hover:bg-primary hover:text-white' : 'bg-gray-200 text-gray-600 hover:bg-primary hover:text-white')
                  ]">
            {{ $t('common.previous') }}
          </button>

          <span :class="['text-sm mx-2', darkMode ? 'text-slate-400' : 'text-gray-600']">
            {{ $t('common.page', { current: pageIndex, total: totalPages }) }}
          </span>

          <button @click="nextPage" :disabled="pageIndex >= totalPages"
                  :class="[
                    'px-3 py-1 rounded transition-colors',
                    pageIndex >= totalPages
                      ? (darkMode ? 'bg-[#232f48] text-slate-400 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed')
                      : (darkMode ? 'bg-[#232f48] text-slate-400 hover:bg-primary hover:text-white' : 'bg-gray-200 text-gray-600 hover:bg-primary hover:text-white')
                  ]">
            {{ $t('common.next') }}
          </button>

          <div class="ml-4 flex items-center gap-2">
            <span :class="['text-sm', darkMode ? 'text-slate-400' : 'text-gray-600']">{{ $t('common.itemsPerPage') }}:</span>
            <select v-model="pageSize" @change="handlePageSizeChange"
                    :class="[
                      'border text-sm rounded-md p-1 focus:ring-primary focus:border-primary',
                      darkMode ? 'bg-[#232f48] border-slate-700 text-white' : 'bg-gray-100 border-gray-300 text-gray-900'
                    ]">
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getApiUrl } from '@/config/apiConfig';

export default {
  name: 'History',
  data() {
    return {
      loading: false,
      error: null,
      sessionStats: [],
      pageIndex: 1,
      pageSize: 10,
      totalCount: 0,
      totalPages: 0,
      currentRoomId: null,
      darkMode: false
    }
  },
  computed: {
    hasRoomConnected() {
      return !!this.currentRoomId;
    }
  },
  mounted() {
    this.loadCurrentRoomId();
    this.fetchSessionStats();
    
    // 从localStorage加载深色模式设置
    this.loadDarkModeSetting();
    
    // 监听storage事件，以便在其他页面切换深色模式时实时更新
    window.addEventListener('storage', this.handleStorageChange);
    
    // 监听自定义事件，以便在同一个标签页内切换深色模式时实时更新
    window.addEventListener('darkModeChanged', this.handleDarkModeChanged);
  },
  
  beforeDestroy() {
    // 移除事件监听器
    window.removeEventListener('storage', this.handleStorageChange);
    window.removeEventListener('darkModeChanged', this.handleDarkModeChanged);
  },
  methods: {
    // 加载当前直播间ID
    loadCurrentRoomId() {
      try {
        // 尝试从多种存储方式获取直播间ID
        const bindingInfo = localStorage.getItem('tiktok-binding-info');
        const roomId = localStorage.getItem('tiktok-live-room-id');
        const currentStreamer = localStorage.getItem('currentStreamer');

        console.log('加载直播间ID - bindingInfo:', bindingInfo, 'roomId:', roomId, 'currentStreamer:', currentStreamer);

        // 优先级：bindingInfo > roomId > currentStreamer
        let targetRoomId = null;
        if (bindingInfo) {
          try {
            const binding = JSON.parse(bindingInfo);
            targetRoomId = binding.roomId || binding.bindingSource;
            console.log('从bindingInfo获取直播间ID:', targetRoomId);
          } catch (error) {
            console.error('解析bindingInfo失败:', error);
          }
        } else if (roomId) {
          targetRoomId = roomId;
          console.log('从roomId获取直播间ID:', targetRoomId);
        } else if (currentStreamer) {
          try {
            const streamerData = JSON.parse(currentStreamer);
            
            // 检查主播的绑定状态和验证状态
            // 如果缓存的主播信息包含绑定状态和验证状态，检查是否符合要求
            if (streamerData.bindingStatus !== undefined && streamerData.verificationStatus !== undefined) {
              if (streamerData.bindingStatus !== 1 || streamerData.verificationStatus !== 1) {
                console.warn('主播绑定状态或验证状态不符合要求，不加载缓存信息:', {
                  bindingStatus: streamerData.bindingStatus,
                  verificationStatus: streamerData.verificationStatus
                });
                
                // 清除无效的缓存
                localStorage.removeItem('currentStreamer');
                console.log('已清除无效的主播缓存信息');
                
                // 不设置直播间ID，让页面显示错误信息
                this.currentRoomId = null;
                return;
              }
            }
            
            targetRoomId = streamerData.roomId || streamerData.name || currentStreamer;
            console.log('从currentStreamer解析获取直播间ID:', targetRoomId);
          } catch (error) {
            targetRoomId = currentStreamer;
            console.log('从currentStreamer直接获取直播间ID:', targetRoomId);
          }
        }

        this.currentRoomId = targetRoomId;
        console.log('最终设置的直播间ID:', this.currentRoomId);
      } catch (error) {
        console.error('加载直播间ID失败:', error);
        this.currentRoomId = null;
      }
    },

    // 获取直播场次统计数据
    async fetchSessionStats() {
      if (!this.currentRoomId) {
        this.error = this.$t('history.noRoomInfo');
        return;
      }

      try {
        this.loading = true;
        this.error = null;

        // 获取katoken用于认证
        const katoken = localStorage.getItem('katoken');
        if (!katoken) {
          this.error = this.$t('history.notLoggedIn');
          this.loading = false;
          return;
        }

        // 构建请求参数
        const requestBody = {
          liveRoomId: this.currentRoomId,
          pageIndex: this.pageIndex,
          pageSize: this.pageSize
        };

        console.log('请求直播场次统计数据，参数:', requestBody);

        // 调用API
        const response = await fetch(getApiUrl('LIVE_SESSION_STATS_LIST'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'req_app_id': 'LivePlugin',
            'req_app_secret': 'ewMpjg7t4vX5bK13oRZzSB8mEuNGr2Yy',
            'katoken': katoken
          },
          body: JSON.stringify(requestBody)
        });

        console.log('API响应状态:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('API请求失败:', response.status, errorText);
          throw new Error(`请求失败: ${response.status}`);
        }

        const data = await response.json();
        console.log('API响应数据:', data);

        if (data.success && data.data && Array.isArray(data.data)) {
          this.sessionStats = data.data;
          this.totalCount = data.totalCount || 0;
          this.totalPages = data.totalPages || Math.ceil(this.totalCount / this.pageSize);
          console.log('成功获取直播场次统计数据，数量:', this.sessionStats.length);
        } else {
          console.warn('获取直播场次统计数据失败或无数据:', data);
          this.sessionStats = [];
          this.totalCount = 0;
          this.totalPages = 0;
        }

      } catch (error) {
        console.error('获取直播场次统计数据失败:', error);
        this.error = error.message || this.$t('history.loadFailedPleaseRetry');
        this.sessionStats = [];
        this.totalCount = 0;
        this.totalPages = 0;
      } finally {
        this.loading = false;
      }
    },

    // 上一页
    prevPage() {
      if (this.pageIndex > 1) {
        this.pageIndex--;
        this.fetchSessionStats();
      }
    },

    // 下一页
    nextPage() {
      if (this.pageIndex < this.totalPages) {
        this.pageIndex++;
        this.fetchSessionStats();
      }
    },

    // 处理每页显示数量变化
    handlePageSizeChange() {
      this.pageIndex = 1; // 重置到第一页
      this.fetchSessionStats();
    },

    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return this.$t('history.unknownTime');
      
      try {
        const date = new Date(dateString.replace(' ', 'T'));
        if (isNaN(date.getTime())) {
          return dateString;
        }
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return `${year}-${month}-${day} ${hours}:${minutes}`;
      } catch (error) {
        console.warn('格式化日期失败:', error);
        return dateString;
      }
    },

    // 格式化时长（秒转换为小时:分钟）
    formatDuration(seconds) {
      if (!seconds || seconds <= 0) return this.$t('history.zeroMinutes');
      
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      
      if (hours > 0) {
        return this.$t('history.hoursMinutes', { hours, minutes });
      } else {
        return this.$t('history.minutes', { minutes });
      }
    },

    // 格式化数字（添加千位分隔符）
    formatNumber(num) {
      if (typeof num !== 'number') return '0';
      return num.toLocaleString('zh-CN');
    },

    // 获取状态样式类
    getStatusClass(status) {
      switch (status) {
        case 1: // 进行中
          return 'bg-green-500/20 text-green-400';
        case 2: // 已结束
          return 'bg-slate-500/20 text-slate-400';
        case 3: // 已取消
          return 'bg-red-500/20 text-red-400';
        default:
          return 'bg-slate-500/20 text-slate-400';
      }
    },

    // 获取状态文本
    getStatusText(status) {
      switch (status) {
        case 1:
          return this.$t('history.statusLive');
        case 2:
          return this.$t('history.statusEnded');
        case 3:
          return this.$t('history.statusCancelled');
        default:
          return this.$t('history.unknownStatus');
      }
    },

    // 查看场次详情 - 跳转到AnalyticsSingle页面
    viewSessionDetails(session) {
      console.log('查看场次详情，跳转到AnalyticsSingle:', session);
      
      // 保存选中的sessionId到localStorage，以便AnalyticsSingle页面使用
      if (session.sessionId) {
        localStorage.setItem('selectedSessionId', session.sessionId);
        console.log('已保存selectedSessionId到localStorage:', session.sessionId);
      }
      
      // 跳转到AnalyticsSingle页面
      this.$router.push('/analytics-single');
    },
    
    // 加载深色模式设置
    loadDarkModeSetting() {
      const savedDarkMode = localStorage.getItem('darkMode');
      if (savedDarkMode !== null) {
        this.darkMode = savedDarkMode === 'true';
      }
    },
    
    // 处理storage变化事件
    handleStorageChange(event) {
      if (event.key === 'darkMode') {
        this.loadDarkModeSetting();
      }
    },
    
    // 处理自定义深色模式变化事件
    handleDarkModeChanged(event) {
      if (event.detail && event.detail.darkMode !== undefined) {
        this.darkMode = event.detail.darkMode;
      } else {
        // 如果事件没有包含darkMode值，重新从localStorage加载
        this.loadDarkModeSetting();
      }
    }
  }
}
</script>

<style scoped>
/* 自定义滚动条样式 - 参考Analytics.vue */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.3) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(156, 163, 175, 0.3);
  border-radius: 4px;
  border: 2px solid transparent;
  background-clip: content-box;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(156, 163, 175, 0.5);
  background-clip: content-box;
}

/* 确保容器高度正确 */
.h-full {
  height: 100%;
}

.flex-1 {
  flex: 1 1 0%;
}

/* 分页按钮样式优化 */
button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* 直播场次卡片悬停效果 */
.bg-\[#232f48\]:hover,
.bg-gray-100:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
}
</style>
